import api from "../services/api";
import { formatApiError } from "./helpers";

export const CARPETA_EDITAR = "Editar";

function levenshtein(a, b) {
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

export function sugerirCarpetasEditar(directorios, esperado = CARPETA_EDITAR) {
  const esperadoNorm = esperado.toLowerCase();
  const candidatos = [];
  for (const d of directorios) {
    if (d === esperado) continue;
    const dn = d.trim().toLowerCase();
    if (!dn) continue;
    if (dn === esperadoNorm) {
      candidatos.push({ nombre: d, distancia: 0, motivo: "solo_difiere_mayusculas" });
      continue;
    }
    const dist = levenshtein(dn, esperadoNorm);
    if (dist <= 2 && dn.length >= 3) {
      candidatos.push({ nombre: d, distancia: dist, motivo: "nombre_parecido" });
    }
  }
  candidatos.sort((a, b) => a.distancia - b.distancia || a.nombre.localeCompare(b.nombre, "es"));
  return candidatos;
}

export function verificarCaptacionDesdeDirectorios(captacion, directorios) {
  if (directorios.includes(CARPETA_EDITAR)) {
    return {
      captacion,
      estado: "ok",
      carpetaEsperada: CARPETA_EDITAR,
      carpetaUsada: CARPETA_EDITAR,
    };
  }

  const sugerencias = sugerirCarpetasEditar(directorios);
  const baseMsg = `No existe la carpeta «${CARPETA_EDITAR}» en «${captacion}».`;

  if (sugerencias.length) {
    return {
      captacion,
      estado: "sugerencia",
      carpetaEsperada: CARPETA_EDITAR,
      sugerencias,
      directoriosEncontrados: directorios,
      mensaje: baseMsg,
    };
  }

  const otras = directorios.length
    ? ` Carpetas en la captación: ${directorios.join(", ")}.`
    : " No hay otras carpetas dentro de la captación.";
  return {
    captacion,
    estado: "falta",
    carpetaEsperada: CARPETA_EDITAR,
    directoriosEncontrados: directorios,
    mensaje: `${baseMsg}${otras}`,
  };
}

async function verificarViaBrowse(captaciones) {
  const resultados = [];
  for (const captacion of captaciones) {
    try {
      const res = await api.get(
        `/multimedia/retoque/nas/browse?path=${encodeURIComponent(captacion)}`,
      );
      const data = res.data;
      if (!data?.ok) {
        resultados.push({
          captacion,
          estado: "error",
          mensaje: formatApiError(data, `No se pudo leer la captación «${captacion}».`),
        });
        continue;
      }
      const directorios = (data.items || [])
        .filter((i) => i.type === "directory")
        .map((i) => i.name);
      resultados.push(verificarCaptacionDesdeDirectorios(captacion, directorios));
    } catch (e) {
      resultados.push({
        captacion,
        estado: "error",
        mensaje: formatApiError(e, `No se pudo leer la captación «${captacion}».`),
      });
    }
  }
  return {
    ok: resultados.every((r) => r.estado === "ok"),
    captaciones: resultados,
  };
}

/** Verifica carpeta Editar: API dedicada si existe; si no, explorador NAS (siempre disponible). */
export async function verificarCarpetasEditar(captaciones) {
  const urls = [
    "/multimedia/retoque/captaciones/verificar-editar",
    "/multimedia/retoque/verificar",
  ];

  for (const url of urls) {
    try {
      const res = await api.post(url, { captaciones });
      const data = res.data;
      // ok:false es normal si hay sugerencias o faltas; lo importante es captaciones[]
      if (Array.isArray(data?.captaciones)) return data;
      if (data && data.ok === false && !data.captaciones) {
        throw new Error(formatApiError(data, "No se pudo verificar las carpetas Editar."));
      }
    } catch (e) {
      if (e.response?.status === 404) continue;
      throw e;
    }
  }

  return verificarViaBrowse(captaciones);
}
